import React, {useEffect, useState} from 'react';
import styles from "./TutorialCreateOrEdit.module.scss";
import {PROD_API_URL} from "../../constants/constants";
import {useSelector, useDispatch} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import TextInput from "../../Components/TextInput/TextInput";
import TextAreaInput from "../../Components/TextAreaInput/TextAreaInput";
import Button, {BUTTON_TYPE_SECONDARY} from "../../Components/Button/Button";
import {PATH_TUTORIAL_ADMINISTRATION} from "../../App";
import DraftEditor from "../../Components/DraftEditor/DraftEditor";
import {NEW_TUTORIAL_ID} from "../TutorialAdministration/Tutorials";
import {hasValue} from "../../helpers/helperFunctions";
import ImageSubmit from "../../Components/ImageSubmit/ImageSubmit";
import toast from "react-hot-toast";
import {errorToast, successToast} from "../../helpers/toastCSS";
import {Checkbox, FormControlLabel, MenuItem, TextField} from "@mui/material";

export const TUTORIAL_TYPE_GENERAL = 'General Tutorial';
export const TUTORIAL_TYPE_VIDEO = 'Video';
export const TUTORIAL_TYPE_ARTICLE = 'Written Guide';
export const TUTORIAL_TYPE_WALKTHROUGH = 'Interactive Walkthrough';
export const TUTORIAL_TYPE_GETTING_STARTED = 'Getting Started';

const tutorialTypes = [
    {
      value: TUTORIAL_TYPE_VIDEO,
      label: 'Video',
    },
    {
      value: TUTORIAL_TYPE_ARTICLE,
      label: 'Written Guide',
    },
    {
      value: TUTORIAL_TYPE_WALKTHROUGH,
      label: 'Interactive Walkthrough',
    },
    {
      value: TUTORIAL_TYPE_GETTING_STARTED,
      label: 'Getting Started',
    },
]

function TutorialCreateOrEdit({provider}) {
  const {id} = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [tutorial, setTutorial] = useState();
  const [openEditorModal, setOpenEditorModal] = useState(false);
  const [openDeleteTutorialModal, setOpenDeleteTutorialModal] = useState(false);

  const [tutorialName, setTutorialName] = useState('');
  const [tutorialDescription, setTutorialDescription] = useState('');
  const [tutorialHTMLContent, setTutorialHTMLContent] = useState('');
  const [tutorialImage, setTutorialImage] = useState('');
  const [tutorialImageBanner, setTutorialImageBanner] = useState('');
  const [tutorialVideo, setTutorialVideo] = useState('');
  const [tutorialType, setTutorialType] = useState(TUTORIAL_TYPE_ARTICLE);
  const [tutorialShowInProduction, setTutorialShowInProduction] = useState(false);
  const [walkthroughPath, setWalkthroughPath] = useState('');

  const tutorialsData = useSelector(state => state.tutorialData?.allTutorials || []);

  useEffect(() => {
    if (id !== NEW_TUTORIAL_ID && !tutorial) {
      fetchTutorial();
    }
  }, [id, tutorialsData]);

  const fetchTutorial = async () => {
    if (id === NEW_TUTORIAL_ID) return;

    try {
      const response = await fetch(`${PROD_API_URL}/tutorial/${id}`, {
        method: "GET",
        headers: {"content-type": "application/json", "accept": "*"},
      });
      const result = await response.json();
      if (result && result.data) {
        setTutorial(result.data[0]);
        setTutorialDefaultData(result.data[0]);
      }
    } catch (error) {
      console.error("Error fetching tutorial:", error);
      toast('Error fetching tutorial data', errorToast);
    }
  };

  const setTutorialDefaultData = (tutorialData) => {
    if (id === NEW_TUTORIAL_ID) {
      return;
    }

    setTutorialName(tutorialData.name || '');
    setTutorialDescription(tutorialData.description || '');
    setTutorialHTMLContent(tutorialData.htmlContent || '');
    setTutorialImage(tutorialData.image || '');
    setTutorialImageBanner(tutorialData.imageBanner || '');
    setTutorialVideo(tutorialData.video || '');
    setTutorialType(tutorialData.type || '');
    setTutorialShowInProduction(tutorialData.showInProduction || false);
    setWalkthroughPath(tutorialData.walkthroughPath || '');
  };

  const handleSaveTutorial = async () => {
    try {
      const body = {
        name: tutorialName,
        description: tutorialDescription,
        htmlContent: tutorialHTMLContent,
        image: tutorialImage,
        imageBanner: tutorialImageBanner,
        video: tutorialVideo,
        type: tutorialType,
        showInProduction: tutorialShowInProduction,
        walkthroughPath: walkthroughPath,
      };

      if (hasValue(id) && id !== NEW_TUTORIAL_ID) {
        body.id = tutorial.id;
      }

      await fetch(PROD_API_URL + "/tutorial/createOrEdit", {
        "method": "POST",
        "headers": {"content-type": "application/json", "accept": "*"},
        "body": JSON.stringify(body)
      }).then(r => {
        if (r.ok && r.status === 200) {
          if (id === NEW_TUTORIAL_ID) {
            toast('Tutorial Successfully Created!', successToast);
            navigate(PATH_TUTORIAL_ADMINISTRATION);
          } else {
            toast('Tutorial Successfully updated!', successToast);
          }
        } else {
          toast('ERROR Saving Tutorial', errorToast);
        }
      });
    } catch (e) {
      console.error(e);
      toast('ERROR Saving Tutorial: ' + e, errorToast);
    }
  };

  const handleDeleteTutorial = async () => {
    try {
      await fetch(`${PROD_API_URL}/tutorials/${id}`, {
        method: "DELETE",
        headers: {"content-type": "application/json", "accept": "*"},
      }).then(r => {
        if (r.ok) {
          toast('Tutorial Successfully deleted!', successToast);
          navigate(PATH_TUTORIAL_ADMINISTRATION);
        } else {
          toast('ERROR Deleting Tutorial', errorToast);
        }
      });
    } catch (e) {
      console.error(e);
      toast('ERROR Deleting Tutorial: ' + e, errorToast);
    }
  };

  return (
    <main id="home" key="home" className={styles.mainTutorialContainer}>
    <DraftEditor open={openEditorModal}
        setClose={() => setOpenEditorModal(false)}
        setFinishedHtml={setTutorialHTMLContent}
        valueHTML={tutorialHTMLContent}/>
      {(tutorial || id === NEW_TUTORIAL_ID) && <div className={styles.tutorialAdminContainer}>
        <div className={styles.tutorialInputContainer}>
          <div className={styles.editRow}>
            <TextInput value={tutorialName} label="Title" setValue={setTutorialName}/>

            <div className={styles.checkboxContainer}>
              <FormControlLabel
                  control={<Checkbox checked={tutorialShowInProduction}
                                     onChange={event => setTutorialShowInProduction(event.target.checked)}/>}
                  label="Show in production"
                  labelPlacement="top"
              />
            </div>
            <div className={styles.buttonContainer}>
              <ImageSubmit base64Value={tutorialImage} setBase64={setTutorialImage} useBase64 text="Tutorial Image"/>
            </div>
          </div>
          <div className={styles.editRow}>
            <div className={styles.checkboxContainer}>
              <TextField
                  id="outlined-select-currency"
                  select
                  label="Select"
                  defaultValue={tutorialType}
                  helperText="Select Tutorial Type">
                {tutorialTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value} onClick={() => setTutorialType(option.value)}>
                      {option.label}
                    </MenuItem>
                ))}
              </TextField>
            </div>
            {tutorialType === TUTORIAL_TYPE_WALKTHROUGH &&
                <div className={styles.halfWidthContainer}> <TextInput value={walkthroughPath} label="Walkthrough Path" setValue={setWalkthroughPath}/> </div>}
          </div>
          <div className={styles.editRow}>
            <TextInput value={tutorialImageBanner} label="Image Banner" setValue={setTutorialImageBanner}/>
            <TextInput value={tutorialVideo} label="Video" setValue={setTutorialVideo}/>

          </div>
          <div className={styles.textAreaInputRow}>
            <div style={{flex: '1'}}>
              <TextAreaInput
                  value={tutorialDescription}
                  setValue={setTutorialDescription}
                  rows={4}
                  label="Description"/>
            </div>
            </div>
          </div>
          <div className={styles.bottomActionContainer}>
            <Button
                className={styles.deleteButton}
                buttonType={BUTTON_TYPE_SECONDARY}
                disabled={!hasValue(id) || id === NEW_TUTORIAL_ID}
                text="DELETE"
                onClick={() => setOpenDeleteTutorialModal(true)}/>
            <div className={styles.flexGrower}/>
            <div style={{marginRight: '64px'}}>
              <Button buttonType={BUTTON_TYPE_SECONDARY} text="Content Editor"
                      onClick={() => setOpenEditorModal(true)}/>
            </div>
            <Button buttonType={BUTTON_TYPE_SECONDARY} text="SAVE" onClick={handleSaveTutorial}/>
          </div>
        </div>
        }
        {openDeleteTutorialModal && (
        <div className={styles.deleteModalContainer}>
          <div className={styles.deleteModal}>
            <h3>Are you sure you want to delete this tutorial?</h3>
            <p>This action cannot be undone.</p>
            <div className={styles.deleteModalButtons}>
              <Button buttonType={BUTTON_TYPE_SECONDARY} text="CANCEL" onClick={() => setOpenDeleteTutorialModal(false)}/>
              <Button buttonType={BUTTON_TYPE_SECONDARY} text="DELETE" onClick={handleDeleteTutorial}/>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default TutorialCreateOrEdit;